// Reusable helpers for player contract grids (admin + public).
// Source of truth for seasons displayed in any contract grid (2024 → 2035-36):
export const CONTRACT_SEASON_OPTIONS = ["2024", "2024-25", "2025-26", "2026-27", "2027-28", "2028-29", "2029-30", "2030-31", "2031-32", "2032-33", "2033-34", "2034-35", "2035-36"];

// Parse a term string ("2 Years", "1 Year", "3-yr") into a whole number of years.
export const termYears = (term) => {
    const m = String(term || "").match(/(\d+)/);
    return m ? Math.max(1, parseInt(m[1], 10)) : 1;
};

// Given a contract-history row's season + term, return the list of seasons it
// covers. Mirrors the backend: a bare offseason YEAR (e.g. "2025") that isn't a
// real season means it takes effect the UPCOMING season ("2025-26"). "2024" is
// a real season so it stays put.
export const contractSpan = (season, term) => {
    let start = String(season || "").trim();
    if (/^\d{4}$/.test(start) && !CONTRACT_SEASON_OPTIONS.includes(start)) {
        const y = Number(start);
        start = `${y}-${String(y + 1).slice(-2)}`;
    }
    const i = CONTRACT_SEASON_OPTIONS.indexOf(start);
    if (i === -1) return [];
    return CONTRACT_SEASON_OPTIONS.slice(i, i + termYears(term));
};


// The calendar YEAR a season column represents (bottom header row).
// "2024" -> "2024"; "2024-25" -> "2025" (the year the season ends).
export const seasonYearLabel = (s) => {
    const m = String(s).match(/^(\d{4})-(\d{2})$/);
    return m ? String(Number(m[1]) + 1) : String(s);
};

// Real-season neighbours ("2025-26" <-> "2026-27" / "2024-25").
export const nextRealSeason = (s) => {
    const m = String(s).match(/^(\d{4})-(\d{2})$/);
    if (!m) return s;
    const y = Number(m[1]) + 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};
export const prevRealSeason = (s) => {
    const m = String(s).match(/^(\d{4})-(\d{2})$/);
    if (!m) return s;
    const y = Number(m[1]) - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};

// Build the offseason/preseason "Camp Invite" context from the master settings.
// Camp invites ONLY exist during offseason or preseason — outside those phases
// this returns { enabled:false } and no camp badge is ever shown.
export const campCtxFromSettings = (settings) => {
    const phase = String(settings?.current_stage || "").toLowerCase();
    const enabled = phase === "offseason" || phase === "preseason";
    if (!enabled) return { enabled: false };
    const cur = String(settings?.current_season || "");
    let campSeason;
    if (/^\d{4}$/.test(cur)) {
        // Bare offseason year (e.g. "2026") → camp is for the upcoming "2026-27".
        const y = Number(cur);
        campSeason = `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
    } else {
        // Preseason → the camp is for the CURRENT season; offseason → the UPCOMING one.
        campSeason = phase === "preseason" ? cur : nextRealSeason(cur);
    }
    return { enabled: true, phase, campSeason, prevSeason: prevRealSeason(campSeason) };
};

// Format a positive number as US currency without the leading $, e.g. 84872.5 -> "84,872.50"
export const formatMoney = (n) => {
    const v = Number(n) || 0;
    return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Non-salary contract statuses available across the site, each with a distinct
// badge colour (kept visually distinct from salary-green and from each other).
export const CONTRACT_STATUSES = ["UFA", "RFA", "Reserve", "SPA", "10 Days"];

export const CONTRACT_STATUS_STYLE = {
    UFA: "bg-[#EF4444]/15 text-[#FCA5A5] border border-[#EF4444]/50",          // red
    RFA: "bg-[#3B82F6]/15 text-[#93C5FD] border border-[#3B82F6]/50",          // blue
    Reserve: "bg-[#F97316]/15 text-[#FDBA74] border border-[#F97316]/50",      // orange
    SPA: "bg-[#EC4899]/15 text-[#F9A8D4] border border-[#EC4899]/50",          // pink
    "10 Days": "bg-[#14B8A6]/15 text-[#5EEAD4] border border-[#14B8A6]/50",    // teal
    SIGNED: "bg-[#22C55E]/20 text-[#86EFAC] border border-[#22C55E]/50",
    RETIRING: "bg-[#F59E0B]/15 text-[#FCD34D] border border-[#F59E0B]/50",
    RETIRED: "bg-[#6B7280]/20 text-[#D1D5DB] border border-[#6B7280]/50",
    "Camp Invite": "bg-[#F6C453]/15 text-[#F6C453] border border-[#F6C453]/55",   // gold — offseason tryout
};

export const statusStyle = (s) => CONTRACT_STATUS_STYLE[s] || "bg-[#1E2233] text-[#94A3B8] border border-[#334155]/60";

// Bucket a player's season status for the Contracts filter:
// "signed" | "ufa" | "rfa" | "reserve" | "spa" | "10days" | "other" | "none"
export function contractStatusBucket(p, season) {
    const cell = cellForPlayerSeason(p, season);
    if (cell.kind === "salary") return "signed";
    if (cell.kind === "status") {
        const v = String(cell.value).toLowerCase().replace(/\s+/g, "");
        if (v === "ufa") return "ufa";
        if (v === "rfa") return "rfa";
        if (v === "reserve") return "reserve";
        if (v === "spa") return "spa";
        if (v === "10days") return "10days";
        if (v === "signed") return "signed";
        return "other";
    }
    return "none";
}

// Resolve a player's cell for a given season — Contract History ONLY (no legacy
// current_salary/contract_status fields). Returns:
//   { kind: "salary", value: Number } | { kind: "status", value } | { kind: "empty" }
export function cellForPlayerSeason(p, season) {
    const rows = (p.contracts || []).filter((r) => r.season === season);
    const salaryRow = rows.find((r) => r.salary && r.salary > 0);
    if (salaryRow) return { kind: "salary", value: salaryRow.salary };
    const statusRow = rows.find((r) => r.status);
    if (statusRow) return { kind: "status", value: statusRow.status };
    return { kind: "empty" };
}

// Resolve ALL contract cells for a season (a player can hold several short
// contracts in one season: e.g. 10 Days → Reserve → SPA). Salary first, then
// status badges ordered by date signed. Falls back to the legacy single cell.
export function cellsForPlayerSeason(p, season, ctx = null) {
    const rows = (p.contracts || []).filter((r) => r.season === season);
    const cells = [];
    rows.forEach((row) => {
        const common = {
            date_signed: row.date_signed || "", team_id: row.team_id || "",
            active: row.active !== false, year: row.contract_year || null, of: row.contract_years || null,
            signing_id: row.signing_id || "",
        };
        if (row.salary && row.salary > 0) cells.push({ kind: "salary", value: row.salary, ...common });
        else if (row.status) cells.push({ kind: "status", value: row.status, ...common });
    });
    if (cells.length) {
        cells.sort((a, b) =>
            (a.kind === "salary" ? -1 : 0) - (b.kind === "salary" ? -1 : 0) ||
            String(a.date_signed).localeCompare(String(b.date_signed)));
        // Carried-on marker: the team the player actually plays (active/green) has
        // a deal signed BEFORE a greyed other-team deal this season -> their deal
        // predated the interruption and resumes (a trade BACK). Render a "<".
        // A former-team deal that simply overlaps (player moved on) just greys out;
        // it does NOT produce an arrow.
        const isPrimary = (c) => c.kind === "salary" || c.value === "SIGNED";
        const active = cells.find((c) => c.active !== false && isPrimary(c));
        const greyOther = cells.find((c) => c.active === false && isPrimary(c) && active && c.team_id !== active.team_id);
        if (active && greyOther && String(active.date_signed) < String(greyOther.date_signed)) {
            cells.push({ kind: "carryover", team_id: active.team_id, active: true, year: active.year, of: active.of });
        }
        return cells;
    }
    // OWNER-ADDED camp invite overrides the auto FA badge for this exact season
    // (gold). Persisted in a hidden store — contract history is never touched.
    const inviteId = ctx && ctx.invites && ctx.invites[`${p.id}|${season}`];
    if (inviteId) return [{ kind: "status", value: "Camp Invite", active: true, _camp: true, _hasInvite: true, _inviteId: inviteId }];
    // A newly-DRAFTED player with no contract yet is an RFA for the season right
    // after their draft (2026 draft → 2026-27; future drafts shift automatically).
    // Signing ANY contract overrides this (the branch above returns the deal).
    const draftYear = Number(p.entry_draft?.year) || null;
    if (draftYear && !(p.contracts || []).length) {
        const rfaSeason = `${draftYear}-${String((draftYear + 1) % 100).padStart(2, "0")}`;
        if (season === rfaSeason) {
            // A freshly-drafted player in their RFA season CAN also be given a
            // training-camp invite when that season is the camp season.
            const canInvite = !!(ctx && ctx.enabled && season === ctx.campSeason);
            return [{ kind: "status", value: "RFA", active: true, _fa: true, ...(canInvite ? { _canInvite: true } : {}) }];
        }
    }
    // No contract this season -> free-agency marker if this is the season right
    // after the player's most recent contract (UFA red default, RFA blue if owner-set).
    // In the upcoming (camp) season the owner can click it to ADD a camp invite
    // that overrides the badge — only when NO contract spans the upcoming season.
    if (isFaSeason(p, season)) {
        const canInvite = !!(ctx && ctx.enabled && season === ctx.campSeason);
        return [{ kind: "status", value: faStatusFor(p), active: true, _fa: true, ...(canInvite ? { _canInvite: true } : {}) }];
    }
    // OFFSEASON / PRESEASON ONLY — auto "Camp Invite" (gold): ANY unsigned player
    // with no contract for the camp season — DRAFTED or undrafted. Covers a
    // prospect drafted in a PRIOR year who still hasn't signed (e.g. a USports /
    // NCAA draftee invited to camp), as well as a player who appeared last season
    // on a tryout/PTO but never held a real contract. Returning players WITH a
    // prior contract are handled by the FA branch above. Never shows outside
    // offseason/preseason.
    if (ctx && ctx.enabled && season === ctx.campSeason) {
        return [{ kind: "status", value: "Camp Invite", active: true, _camp: true }];
    }
    return [];
}

// A season the player is "covered" for — any real contract entry: salaried,
// history SIGNED, or a short-term deal (10 Days / SPA / Reserve). The UFA/RFA
// marker lands the season AFTER the last covered one, so it also follows a
// 10-day, SPA or Reserve deal.
const isContractedSeason = (p, s) => {
    const c = cellForPlayerSeason(p, s);
    if (c.kind === "salary") return true;
    if (c.kind === "status") return ["SIGNED", "10 Days", "SPA", "Reserve"].includes(c.value);
    return false;
};

// The player's chosen free-agency label (owner-toggleable), default UFA.
export const faStatusFor = (p) => (p.fa_status === "RFA" ? "RFA" : "UFA");

// True when `season` is exactly the one following the player's LAST contracted
// season — i.e. where their UFA/RFA badge belongs. Moves automatically when a
// new contract is signed (the last contracted season shifts later).
export function isFaSeason(p, season) {
    let last = -1;
    for (let i = 0; i < CONTRACT_SEASON_OPTIONS.length; i++) {
        if (isContractedSeason(p, CONTRACT_SEASON_OPTIONS[i])) last = i;
    }
    if (last < 0) return false;
    return CONTRACT_SEASON_OPTIONS[last + 1] === season;
}

// Group a player's contiguous SALARY seasons into "contracts" (a multi-year deal).
// Each run = one connected bar in the grid and one row in Contract History.
//   { start, end, years, seasons:[...], salaries:[...], total, aav, date_signed }
export function playerContractRuns(p, seasons = CONTRACT_SEASON_OPTIONS) {
    const byS = {};
    (p.contracts || []).forEach((r) => { byS[r.season] = r; });
    const runs = [];
    let cur = null;
    seasons.forEach((s) => {
        const cell = cellForPlayerSeason(p, s);
        if (cell.kind === "salary") {
            const signed = byS[s]?.date_signed || "";
            if (!cur) {
                cur = { start: s, end: s, seasons: [s], salaries: [cell.value], total: cell.value, date_signed: signed };
            } else {
                cur.end = s; cur.seasons.push(s); cur.salaries.push(cell.value); cur.total += cell.value;
                if (!cur.date_signed && signed) cur.date_signed = signed;
            }
        } else if (cur) {
            runs.push(cur); cur = null;
        }
    });
    if (cur) runs.push(cur);
    return runs.map((r) => ({ ...r, years: r.seasons.length, aav: r.total / r.seasons.length }));
}

// Where a player currently sits within their multi-year salary deal.
// Anchors on `anchorSeason` (the active season). Returns { year, of, run } for
// the run containing that season, else the most recent run starting on/before
// it. Returns null when the player has no salaried deal.
export function currentContractYear(p, anchorSeason) {
    const runs = playerContractRuns(p);
    if (!runs.length) return null;
    let run = runs.find((r) => r.seasons.includes(anchorSeason));
    if (!run) {
        const before = runs.filter((r) => r.start <= anchorSeason);
        run = before.length ? before[before.length - 1] : runs[0];
    }
    const idx = run.seasons.indexOf(anchorSeason);
    const year = idx >= 0 ? idx + 1 : (anchorSeason > run.end ? run.years : 1);
    return { year: Math.min(Math.max(year, 1), run.years), of: run.years, run };
}

// The "Year X of Y" position of a season within ITS OWN contract. A contract is
// a distinct signing — identified by team + date signed — NOT merely contiguous
// contracted seasons. Two separate deals (e.g. a Boston 3-yr then a Hamilton
// 3-yr) each count 1..3 independently; they never merge into one 6-year run.
// Returns { year, of } for a contracted season, else null.
export function contractYearInRun(p, season) {
    const rows = (p.contracts || []).filter((r) => r.season === season && r.contract_year && ((r.salary && r.salary > 0) || r.status === "SIGNED"));
    if (rows.length) {
        const active = rows.find((r) => r.active !== false) || rows[0];
        return { year: active.contract_year, of: active.contract_years };
    }
    // Legacy fallback (no per-season contract rows): use the single salary window.
    const cell = cellForPlayerSeason(p, season);
    if (cell.kind !== "salary") return null;
    const i = CONTRACT_SEASON_OPTIONS.indexOf(season);
    let start = i, end = i;
    const contracted = (s) => cellForPlayerSeason(p, s).kind === "salary";
    while (start > 0 && contracted(CONTRACT_SEASON_OPTIONS[start - 1])) start -= 1;
    while (end < CONTRACT_SEASON_OPTIONS.length - 1 && contracted(CONTRACT_SEASON_OPTIONS[end + 1])) end += 1;
    return { year: i - start + 1, of: end - start + 1 };
}

// Position of a season cell within its contiguous salary run: "single" | "start" | "mid" | "end" | null
export function salaryRunPos(p, season, seasons = CONTRACT_SEASON_OPTIONS) {
    if (cellForPlayerSeason(p, season).kind !== "salary") return null;
    const i = seasons.indexOf(season);
    const prev = i > 0 && cellForPlayerSeason(p, seasons[i - 1]).kind === "salary";
    const next = i < seasons.length - 1 && cellForPlayerSeason(p, seasons[i + 1]).kind === "salary";
    if (prev && next) return "mid";
    if (prev) return "end";
    if (next) return "start";
    return "single";
}

// ── Detailed-view helpers ───────────────────────────────────────────────────
// Position bucket for the Detailed contracts view: "F" | "D" | "G".
export function positionGroup(p) {
    const pos = String(p.pref_position || p.position || "").toUpperCase();
    if (pos === "G" || pos.includes("GOAL")) return "G";
    if (pos.startsWith("D") || pos === "LD" || pos === "RD") return "D";
    return "F";
}
export const POSITION_GROUP_LABEL = { F: "Forwards", D: "Defenders", G: "Goalies" };
export const POSITION_GROUP_ORDER = ["F", "D", "G"];

// Remaining contracted term from `fromSeason` onward (contiguous, inclusive).
// Counts salaried AND status-signed seasons (SIGNED / 10 Days / SPA / Reserve),
// so a signed deal with no dollar amount still contributes to length.
export function remainingTerm(p, fromSeason) {
    const i = CONTRACT_SEASON_OPTIONS.indexOf(fromSeason);
    if (i < 0) return 0;
    let n = 0;
    for (let j = i; j < CONTRACT_SEASON_OPTIONS.length; j++) {
        if (isContractedSeason(p, CONTRACT_SEASON_OPTIONS[j])) n += 1;
        else break;
    }
    return n;
}

// Contract-type sort rank for a season: SGN → SPA → RES → 10 → RFA → UFA → Camp Invite.
const _TYPE_RANK = { signed: 0, spa: 1, reserve: 2, "10days": 3, rfa: 4, ufa: 5, campinvite: 6 };
export function contractTypeRank(p, season, ctx = null) {
    const cells = cellsForPlayerSeason(p, season, ctx);
    const primary = cells.find((c) => c.active !== false) || cells[0];
    if (!primary) return 9;
    if (primary.kind === "salary") return 0;
    const v = String(primary.value || "").toLowerCase().replace(/\s+/g, "");
    return _TYPE_RANK[v] != null ? _TYPE_RANK[v] : 5;
}

// The human status label for a signed deal: "Signed" (new to team), "Re-signed"
// (same team held a prior deal), or "Extended" (signed while still under contract).
export function signingLabel(p, season) {
    const rows = (p.contracts || []).filter((r) => r.season === season && ((r.salary && r.salary > 0) || r.status === "SIGNED") && r.active !== false);
    const row = rows[0];
    if (!row) return "Signed";
    const team = row.team_id;
    const signed = String(row.date_signed || "");
    const runs = playerContractRuns(p);
    const thisRun = runs.find((r) => r.seasons.includes(season));
    // Extension: a later deal signed while an earlier same-team deal was still running.
    const earlierSameTeam = (p.contracts || []).some((r) =>
        r.team_id === team && r.date_signed && signed && String(r.date_signed) < signed && (r.salary > 0 || r.status === "SIGNED"));
    if (thisRun && earlierSameTeam) {
        const startIdx = CONTRACT_SEASON_OPTIONS.indexOf(thisRun.start);
        const prevSeason = CONTRACT_SEASON_OPTIONS[startIdx - 1];
        const heldPrev = prevSeason && (p.contracts || []).some((r) => r.season === prevSeason && r.team_id === team && (r.salary > 0 || r.status === "SIGNED"));
        return heldPrev ? "Extended" : "Re-signed";
    }
    return earlierSameTeam ? "Re-signed" : "Signed";
}

// Total ACTIVE salary across players for one season (for the cap-overview row).
export function seasonSalaryTotal(players, season) {
    let total = 0;
    (players || []).forEach((p) => {
        const rows = (p.contracts || []).filter((r) => r.season === season && r.salary > 0 && r.active !== false);
        if (rows.length) total += Number(rows[0].salary) || 0;
    });
    return total;
}

// Compact cap money, e.g. 1_300_000 -> "$1.3m", 84_000 -> "$84k", 0 -> "—".
export function formatCap(n) {
    const v = Number(n) || 0;
    if (v <= 0) return "—";
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}m`;
    if (v >= 1e3) return `$${Math.round(v / 1e3)}k`;
    return `$${v.toLocaleString("en-US")}`;
}
